import { RootState } from '@store/store'
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

type SortDirection = 'asc' | 'desc'

export type SortParams = {
  fieldId: string
  direction: SortDirection
  viewId: string
}

export type RemoveSort = {
  index: number
  viewId: string
}

export type Sort = Omit<SortParams, 'viewId'>

type State = {
  params: Record<string, Sort[]>
}

const initialState: State = {
  params: {},
}

export const sortingSlice = createSlice({
  name: 'sorting',
  initialState,
  reducers: {
    addSortParams: (state, action: PayloadAction<SortParams & { index: number }>) => {
      const { fieldId, viewId, direction, index } = action.payload
      const viewParams = state.params[viewId] ?? []
      const alreadyAdded = Boolean(viewParams[index])

      if (alreadyAdded) {
        state.params[viewId][index] = {
          fieldId,
          direction,
        }
      } else {
        viewParams.push({
          fieldId,
          direction,
        })

        state.params[viewId] = [...viewParams]
      }
    },
    removeSortParams: (state, action: PayloadAction<RemoveSort>) => {
      const { viewId, index } = action.payload

      state.params[viewId] = state.params[viewId].filter((_, paramIndx) => paramIndx !== index)
    },
    resetParams: (state, action: PayloadAction<string>) => {
      state.params[action.payload] = []
    },
    cleanupSortParams: (state, action: PayloadAction<string>) => {
      // when a field is deleted, we need to clean up the sort params to ensure we don't keep around invalid
      // sort params that reference a field that no longer exists
      const fieldIdToRemove = action.payload

      // Loop through all views and remove any sort params that reference the deleted field
      Object.keys(state.params).forEach(viewId => {
        state.params[viewId] = state.params[viewId].filter(sortParam => sortParam.fieldId !== fieldIdToRemove)
      })
    },
  },
})

export const { addSortParams, removeSortParams, resetParams, cleanupSortParams } = sortingSlice.actions

export default sortingSlice.reducer

/**
 * @name getSortParams
 * @description: Retuns an array of selected sorting params for a specified View. Defaults to an empty array, if no sorting options are set
 */
export const getSortParams = createSelector(
  [(state: RootState) => state?.sorting?.params, (_, props) => props.viewId],
  (sortingParams, viewId: string) => {
    if (sortingParams[viewId]) {
      return sortingParams[viewId]
    } else {
      return []
    }
  }
)

/**
 * @name getValidSortParams
 * @description Returns only those sort parameters that are complete and can be applied:
 *  - fieldId is truthy (non-empty string)
 *  - direction is either 'asc' or 'desc'
 */
export const getValidSortParams = createSelector(
  [getSortParams],
  (sortParams: { fieldId: string; direction: string }[]) => {
    return sortParams.filter(param => {
      const hasField = Boolean(param.fieldId)
      const hasValidDirection = ['asc', 'desc'].includes(param.direction)

      return hasField && hasValidDirection
    })
  }
)
